import type { Patient, PatientDTO } from "@akina/domain";
import type { Tenant } from "../auth";
import { patientMapper } from "../mappers";
import { ApiMethod, uploadFileToS3, type ApiConfiguration } from "./shared";
import { toCamelCase, toSnakeCase } from "@akina/utils";
import type { TermsAndConditionsRequest, TermsAndConditionsResponse, TermsAndConditionsResponseApi } from "types/schemas";

export const patientApi = (config: ApiConfiguration) => ({
  async get(): Promise<Patient> {
    const result = await config.httpService.fetch<PatientDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.patients.get,
    });

    return patientMapper.toDomain(result);
  },
  async update(patient: Patient, signal?: AbortSignal): Promise<Patient> {
    await config.httpService.fetch<PatientDTO>({
      method: ApiMethod.PATCH,
      url: config.endpoints.patients.update,
      data: patientMapper.toDTO(patient),
      signal,
    });

    return await this.get();
  },
  async updateProfilePicture(newProfilePicture: File): Promise<void> {
    const result = await config.httpService.fetch<{ presigned_url: string }>({
      method: ApiMethod.PATCH,
      url: config.endpoints.patients.profilePicture.uploadUrl,
      data: { file_type: newProfilePicture.type },
    });

    await uploadFileToS3(result.presigned_url, newProfilePicture);
  },
  async deleteProfilePicture(): Promise<void> {
    await config.httpService.fetch<string>({
      method: ApiMethod.DELETE,
      url: config.endpoints.patients.profilePicture.delete,
    });
  },
  async getTermsAcceptedStatus(): Promise<TermsAndConditionsResponse> {
    const response = await config.httpService.fetch<TermsAndConditionsResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.patients.getTermsAndConditions,
    });
    return toCamelCase(response);
  },
  async emailAlreadyExists(email: string, tenant: Tenant): Promise<boolean> {
    try {
      const response = await config.httpService.fetch<{ exists: boolean }>({
        method: ApiMethod.GET,
        url: config.endpoints.patients.emailAlreadyExists(email, tenant),
      });
      return response.exists;
    } catch (_) {
      return false;
    }
  },
  async updateTermsAcceptedStatus(status: TermsAndConditionsRequest): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: `${config.endpoints.patients.acceptTermsAndConditions}`,
      data: toSnakeCase(status),
    });
  },
});
